<template>
  <div>
    <div v-if="documentData.badges.length" class="dte-standard-badges">
      <div
        v-for="badge in documentData.badges"
        :key="badge.label"
        class="dte-standard-alert"
      >
        {{ badge.label }}
      </div>
    </div>

    <div class="dte-standard-header">
      <section class="dte-standard-header__issuer">
        <div class="dte-standard-issuer">
          <div class="dte-standard-issuer__logo">
            <img
              v-if="documentData.issuer.logoUrl"
              :src="documentData.issuer.logoUrl"
              alt="Logo emisor"
              class="dte-standard-logo"
            />
          </div>

          <div class="dte-standard-issuer__content">
            <div
              v-if="documentData.issuer.billedInfoText"
              class="dte-standard-issuer__text dte-standard-issuer__text--pre"
            >
              {{ documentData.issuer.billedInfoText }}
            </div>

            <template v-else>
              <div class="dte-standard-issuer__line dte-standard-issuer__line--business">
                {{ documentData.issuer.businessName }}
              </div>
              <div v-if="documentData.issuer.tradeName" class="dte-standard-issuer__line">
                {{ documentData.issuer.tradeName }}
              </div>
              <div v-if="documentData.issuer.activity" class="dte-standard-issuer__line">
                {{ documentData.issuer.activity }}
              </div>
              <div v-if="documentData.issuer.address" class="dte-standard-issuer__line">
                {{ documentData.issuer.address }}
              </div>
              <div v-if="documentData.issuer.phone" class="dte-standard-issuer__line">
                {{ documentData.issuer.phoneLabel }} {{ documentData.issuer.phone }}
              </div>
              <div v-if="documentData.issuer.mobile" class="dte-standard-issuer__line">
                Whatsapp. {{ documentData.issuer.mobile }}
              </div>
              <div v-if="documentData.issuer.email" class="dte-standard-issuer__line">
                {{ documentData.issuer.emailLabel }} {{ documentData.issuer.email }}
              </div>
            </template>

            <div class="dte-standard-issuer__line">
              NIT: {{ documentData.issuer.nit }}
            </div>
            <div class="dte-standard-issuer__line">
              NRC: {{ documentData.issuer.nrc }}
            </div>
          </div>
        </div>
      </section>

      <section class="dte-standard-header__document">
        <table class="dte-standard-table dte-standard-table--boxed dte-standard-table--meta">
          <tbody>
            <tr class="dte-standard-table__title-row" :style="titleRowStyle">
              <td colspan="4" class="dte-standard-table__title-cell">
                {{ documentData.meta.invoiceTypeLabel.toUpperCase() }}
              </td>
            </tr>
            <tr>
              <td colspan="2" class="dte-standard-table__label">Codigo generacion:</td>
              <td colspan="2">{{ documentData.meta.generationCode }}</td>
            </tr>
            <tr>
              <td colspan="2" class="dte-standard-table__label">Sello de recepcion:</td>
              <td colspan="2">{{ documentData.meta.receiptSeal }}</td>
            </tr>
            <tr v-if="showControlNumber">
              <td colspan="2" class="dte-standard-table__label">Numero de control:</td>
              <td colspan="2">{{ documentData.meta.controlNumber }}</td>
            </tr>
            <tr>
              <td colspan="2" class="dte-standard-table__label">Fecha emision:</td>
              <td colspan="2">{{ documentData.meta.issueDate }}</td>
            </tr>
            <tr v-if="showDueDate">
              <td colspan="2" class="dte-standard-table__label">{{ dueDateLabel }}:</td>
              <td colspan="2">{{ documentData.meta.dueDate }}</td>
            </tr>
            <tr>
              <td colspan="2" class="dte-standard-table__label">Correlativo factura:</td>
              <td colspan="2">{{ documentData.meta.invoiceId }}</td>
            </tr>
            <tr v-if="showInternalCorrelative && documentData.meta.correlative">
              <td colspan="2" class="dte-standard-table__label">Correlativo interno:</td>
              <td colspan="2">{{ documentData.meta.invoiceType }}-{{ documentData.meta.correlative }}</td>
            </tr>
            <tr>
              <td colspan="4" class="dte-standard-table__qr">
                <img
                  v-if="documentData.qrImage"
                  :src="documentData.qrImage"
                  alt="QR documento"
                  class="dte-standard-qr"
                />
                <div
                  v-else
                  class="dte-standard-qr dte-standard-qr--placeholder"
                  aria-hidden="true"
                ></div>
              </td>
            </tr>
          </tbody>
        </table>

        <table class="dte-standard-table dte-standard-table--plain dte-standard-table--document-extra">
          <tbody>
            <tr>
              <td class="dte-standard-table__label">Modelo de facturacion:</td>
              <td>{{ documentData.meta.modelLabel }}</td>
              <td class="dte-standard-table__label">Tipo de transmision:</td>
              <td>{{ documentData.meta.transmissionLabel }}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <section v-if="showInlineBanner" class="dte-standard-banner">
      <img
        :src="documentData.issuer.bannerUrl"
        alt="Banner emisor"
        class="dte-standard-banner__image"
      />
    </section>
  </div>
</template>

<script>
import {
  getStandardShowInternalCorrelative,
  getStandardThemeColors,
} from "../utils/dtePrintStandard";
import {
  getDteTemplateLabel,
  isDteTemplateFieldVisible,
} from "@/helpers/dteTemplateSettings";

export default {
  name: "DteStandardHeaderSection",
  props: {
    documentData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    templateSettings() {
      return this.documentData.templateSettings || {};
    },
    showInternalCorrelative() {
      return getStandardShowInternalCorrelative();
    },
    showDueDate() {
      return (
        Boolean(this.documentData.meta.dueDate) &&
        isDteTemplateFieldVisible(this.templateSettings, "document", "dueDate") && 
        !this.isContadoEspecial
      );
    },
    dueDateLabel() {
      return getDteTemplateLabel(this.templateSettings, "dueDate", "Fecha vencimiento");
    },
    titleRowStyle() {
      const colors = getStandardThemeColors(this.documentData);
      return {
        backgroundColor: colors.bg,
        color: colors.fg,
      };
    },
    showInlineBanner() {
      return Boolean(this.documentData.showInlineBanner);
    },
    showControlNumber() {
      return this.documentData?.meta?.invoiceType !== "FER" && Boolean(this.documentData?.meta?.controlNumber);
    },
    isContadoEspecial() {
      const diasCredito = this.diferentBetweenDays(
          this.documentData.meta.issueDate,
          this.documentData.meta.dueDate
      );

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      return (
        user?.showcashinvoiceswhen1day &&
        this.documentData?.meta?.paymentStatus === "Crédito" &&
        diasCredito === 1
      );
    },
  },
  methods: {
    diferentBetweenDays(date1, date2) {
        const [d1, m1, a1] = date1.split('/').map(Number);
        const [d2, m2, a2] = date2.split('/').map(Number);

        const f1 = new Date(a1, m1 - 1, d1);
        const f2 = new Date(a2, m2 - 1, d2);

        const diferenciaMs = Math.abs(f2 - f1);
        return Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));
    }
  }
};
</script>
